import { useEffect, useState } from 'react';
import { isChunkLoadError } from '@/lib/chunkLoadError';

/**
 * How long we show the lightweight loading state for a chunk/dynamic-import
 * failure before giving up and surfacing the real error card. The chunkReload
 * guard (lib/chunkReload.ts) reloads the page on `vite:preloadError`, so the
 * normal outcome is that this component unmounts well before the timer fires.
 * If the reload never happens (a genuinely stuck or looping failure), the
 * escalation guarantees the user still sees an actionable error.
 */
const CHUNK_ERROR_ESCALATION_MS = 4000;

interface ErrorFallbackProps {
  error: unknown;
  resetError: () => void;
}

/**
 * Lightweight loading state shown for auto-recovering chunk-load failures,
 * styled to match the app's route-loader look over the brand background.
 */
function ChunkLoadingState() {
  return (
    <div
      role="status"
      data-testid="chunk-loading"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/leaf-pattern-bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: '2rem',
        fontFamily: 'Kanit, system-ui, sans-serif',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1.25rem',
          background: 'white',
          borderRadius: '16px',
          boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          padding: '2.5rem 3rem',
        }}
      >
        <div
          className="animate-spin"
          aria-hidden
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#8B7355',
            borderRadius: '50%',
          }}
        />
        <p style={{ margin: 0, color: '#6b7280', fontSize: '1rem' }}>Cargando…</p>
      </div>
    </div>
  );
}

/**
 * Custom Sentry.ErrorBoundary fallback. Generic errors (including React #185)
 * paint the "Algo salió mal" card immediately. Chunk/dynamic-import errors
 * instead show a loader — the chunkReload guard reloads the page momentarily —
 * escalating to the error card only if no reload has happened after a few
 * seconds.
 */
export function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const [showLoading, setShowLoading] = useState(() => isChunkLoadError(error));

  useEffect(() => {
    if (!showLoading) return;
    const timer = setTimeout(() => setShowLoading(false), CHUNK_ERROR_ESCALATION_MS);
    return () => clearTimeout(timer);
  }, [showLoading]);

  if (showLoading) {
    return <ChunkLoadingState />;
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundImage: 'url(/leaf-pattern-bg.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      padding: '2rem',
      fontFamily: 'Kanit, system-ui, sans-serif',
    }}>
      <div style={{
        maxWidth: '600px',
        width: '100%',
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        padding: '3rem 2rem',
        textAlign: 'center',
      }}>
        {/* Logo */}
        <img
          src="https://casa-aragon-docs.sfo3.cdn.digitaloceanspaces.com/images/branding/logos/light-backgrounds/logo-fondo-claro.png"
          alt="Casa Aragón"
          style={{
            width: '200px',
            maxWidth: '200px',
            height: 'auto',
            margin: '0 auto 2rem auto',
            display: 'block',
          }}
        />

        {/* Error Icon */}
        <div style={{
          width: '64px',
          height: '64px',
          margin: '0 auto 1.5rem auto',
          borderRadius: '50%',
          background: '#FEF2F2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#DC2626"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>

        {/* Heading */}
        <h1 style={{
          margin: '0 0 0.75rem 0',
          color: '#1f2937',
          fontSize: '1.875rem',
          fontWeight: '600',
          lineHeight: '1.2',
        }}>
          Algo salió mal
        </h1>

        {/* Description */}
        <p style={{
          margin: '0 0 2rem 0',
          color: '#6b7280',
          fontSize: '1rem',
          lineHeight: '1.6',
        }}>
          Lo sentimos, ha ocurrido un error inesperado. Nuestro equipo ha sido notificado automáticamente. Por favor, intenta recargar la página.
        </p>

        {/* Error Details (only in dev) */}
        {import.meta.env.DEV && (
          <details style={{
            marginBottom: '2rem',
            textAlign: 'left',
          }}>
            <summary style={{
              cursor: 'pointer',
              color: '#8B7355',
              fontSize: '0.875rem',
              fontWeight: '500',
              marginBottom: '0.5rem',
            }}>
              Detalles técnicos (solo en desarrollo)
            </summary>
            <pre style={{
              textAlign: 'left',
              padding: '1rem',
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              overflow: 'auto',
              fontSize: '0.875rem',
              color: '#374151',
              margin: '0.5rem 0 0 0',
            }}>
              {error instanceof Error ? error.message : 'Unknown error'}
            </pre>
          </details>
        )}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}>
          <button
            onClick={resetError}
            style={{
              padding: '0.75rem 2rem',
              background: '#8B7355',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'background 0.2s',
              fontFamily: 'Kanit, sans-serif',
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#6f5c44'}
            onMouseOut={(e) => e.currentTarget.style.background = '#8B7355'}
          >
            Intentar de nuevo
          </button>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              padding: '0.75rem 2rem',
              background: 'white',
              color: '#8B7355',
              border: '2px solid #8B7355',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontFamily: 'Kanit, sans-serif',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#f9fafb';
              e.currentTarget.style.borderColor = '#6f5c44';
              e.currentTarget.style.color = '#6f5c44';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.borderColor = '#8B7355';
              e.currentTarget.style.color = '#8B7355';
            }}
          >
            Ir al inicio
          </button>
        </div>

        {/* Contact Info */}
        <p style={{
          marginTop: '2rem',
          paddingTop: '2rem',
          borderTop: '1px solid #e5e7eb',
          color: '#9ca3af',
          fontSize: '0.875rem',
          lineHeight: '1.5',
        }}>
          {/* //TODO: */}
          Si el problema persiste, contáctanos:<br />
          <a href="tel:+573172885216" style={{ color: '#8B7355', textDecoration: 'none' }}>
            +57 317 288 5216
          </a>
        </p>
      </div>
    </div>
  );
}
